import {
  type CreateTamaguiProps,
  createTamagui,
  createTokens,
  setupDev,
} from "tamagui";

import { config as defaultConfig } from "@tamagui/config/v3";
import { shorthands } from "@tamagui/shorthands";

import { animations } from "./config/animations";
import {
  bodyFont,
  degularFont,
  fontSize,
  headingFont,
  khTekaFont,
} from "./config/fonts";
import { media, mediaQueryDefaultActive } from "./config/media";
import { themes as generatedThemes } from "./themes/theme-generated";
import { color } from "./themes/token-colors";
import {
  displayFontSize,
  displayLineHeight,
  khTekaFontSize,
  khTekaLineHeight,
} from "./themes/token-fontSizes";
import { radius } from "./themes/token-radius";
import { size } from "./themes/token-size";
import { space } from "./themes/token-space";
import { zIndex } from "./themes/token-z-index";

// Combined fontSize tokens (legacy numeric + v2 semantic tokens)
const fontSizes = {
  ...fontSize,
  ...displayFontSize,
  ...khTekaFontSize,
};

// Combined lineHeight tokens (v2 semantic tokens)
const lineHeights = {
  ...displayLineHeight,
  ...khTekaLineHeight,
};

// Hold down Option for a second to see some helpful visuals
setupDev({
  visualizer: true,
});

/**
 * This avoids shipping themes as JS. Instead, Tamagui will hydrate them from CSS.
 *
 * Themes:
 * - "light" - Legacy theme with old tokens
 * - "v2" - Temporary name for the new design system theme
 */
const themes =
  process.env.TAMAGUI_OPTIMIZE_THEMES === "true"
    ? ({} as typeof generatedThemes)
    : generatedThemes;

export const config = createTamagui({
  ...defaultConfig,
  themes,
  animations,
  // We only have a single theme, so this is not needed.
  shouldAddPrefersColorThemes: false,
  themeClassNameOnRoot: true,
  mediaQueryDefaultActive,
  shorthands,
  fonts: {
    // Legacy fonts
    heading: headingFont,
    body: bodyFont,

    display: degularFont,
    text: khTekaFont,
  },
  tokens: createTokens({
    color,
    radius,
    zIndex,
    space,
    size,
    fontSize: fontSizes,
    lineHeight: lineHeights,
  }),
  media,
  settings: {
    allowedStyleValues: "somewhat-strict",
    autocompleteSpecificTokens: "except-special",
    fastSchemeChange: false,
    maxDarkLightNesting: 1,
    onlyAllowShorthands: false,
  },
} satisfies CreateTamaguiProps);

export default config;
